import {
  addMonthsToDate,
  addYearsToDate,
  formatDate,
  getDateDifference,
  getStartOfDay,
  getStartOfMonth,
} from '@travelata-libs/utils';
import { computed, type InjectionKey, provide, inject, ref, type Ref, shallowRef, watch } from 'vue';
import {
  type CalendarSystemModelValue,
  type CalendarModelValue,
  MonthName,
  type DateTimestamp,
  type DateISO,
} from '../Calendar.types';
import { convertModelValueToTimestamp, convertModelValueToTextString } from '../utils/date';
import { getCalendarEvents, useRangeEndSelectionCalendarEvents } from '../utils/events';

interface CalendarDraft {
  source: CalendarSystemModelValue;
  value: CalendarSystemModelValue;
  rangeStart: DateTimestamp | null;
}

const CALENDAR_KEY: InjectionKey<ReturnType<typeof useCalendarModel>> = Symbol('SelectionModel');

export function useCalendarModel(
  range: Readonly<Ref<boolean>>,
  model: Readonly<Ref<CalendarModelValue>>,
  visibleMonths: Readonly<Ref<number>> = ref(1),
) {
  const modelTimestamps = computed(() => convertModelValueToTimestamp(range.value, model.value));

  const draft = shallowRef<CalendarDraft | null>(null);
  const currentDraft = computed(() => (draft.value?.source === modelTimestamps.value ? draft.value : null));

  const getModelRangeStart = (): DateTimestamp | null => {
    const value = modelTimestamps.value;
    if (!range.value || value === null) {
      return null;
    }
    const start = getStartOfDay(value.start);
    return start === getStartOfDay(value.end) ? start : null;
  };

  const writeDraft = (patch: Partial<Omit<CalendarDraft, 'source'>>): void => {
    const current = currentDraft.value;
    draft.value = {
      source: modelTimestamps.value,
      value: current ? current.value : modelTimestamps.value,
      rangeStart: current ? current.rangeStart : getModelRangeStart(),
      ...patch,
    };
  };

  const systemModelValue = computed<CalendarSystemModelValue>({
    get: () => (currentDraft.value ? currentDraft.value.value : modelTimestamps.value),
    set: (value) => writeDraft({ value }),
  });

  const rangeStart = computed<DateTimestamp | null>({
    get: () => (currentDraft.value ? currentDraft.value.rangeStart : getModelRangeStart()),
    set: (value) => writeDraft({ rangeStart: value }),
  });

  const calendarEvents = computed(() =>
    rangeStart.value === null ? getCalendarEvents(range.value) : useRangeEndSelectionCalendarEvents(rangeStart.value),
  );

  const displayedDate = ref<DateTimestamp>(Date.now());

  const updateDisplayedDateByModelValue = () => {
    if (!systemModelValue.value || !systemModelValue.value.start) return;

    displayedDate.value = systemModelValue.value.start;
  };

  const submitText = computed(() => convertModelValueToTextString(range.value, systemModelValue.value));

  const getYear = (year: DateTimestamp | DateISO | Date): number => {
    return Number(formatDate(year, 'yyyy'));
  };

  const defaultMinYear = getYear(addYearsToDate(Date.now(), -90));
  const defaultMaxYear = getYear(addYearsToDate(Date.now(), 10));

  const getYearsRange = (minDate: Date | null = null, maxDate: Date | null = null) => {
    const minimumYear = minDate ? getYear(minDate) : defaultMinYear;
    const maximumYear = maxDate ? getYear(maxDate) : defaultMaxYear;

    return Array.from({ length: maximumYear - minimumYear + 1 }, (_, i) => minimumYear + i);
  };

  const getRange = (minDate: Date | null = null, maxDate: Date | null = null) => {
    return getYearsRange(minDate, maxDate).flatMap((year) =>
      Object.values(MonthName).map((month) => `${month} ${year}`),
    );
  };

  const displayedMonth = computed(() => {
    const month = formatDate(displayedDate.value, 'LLLL');

    return month.charAt(0).toUpperCase() + month.slice(1);
  });

  const displayedYear = computed(() => getYear(displayedDate.value));
  const displayedMonthYear = computed(() => `${displayedMonth.value} ${displayedYear.value}`);

  const rangeDisplayedMonthYear = computed(() => {
    const monthYear = formatDate(addMonthsToDate(displayedDate.value, 1), 'LLLL yyyy');

    return monthYear.charAt(0).toUpperCase() + monthYear.slice(1);
  });

  const isStartVisible = (): boolean => {
    const start = systemModelValue.value?.start;
    if (!start) {
      return false;
    }
    const monthOffset = getDateDifference(
      getStartOfMonth(start),
      getStartOfMonth(displayedDate.value),
      'months',
      false,
    );
    return monthOffset >= 0 && monthOffset < visibleMonths.value;
  };

  watch(modelTimestamps, () => {
    if (!isStartVisible()) {
      updateDisplayedDateByModelValue();
    }
  });

  const service = {
    systemModelValue,
    displayedDate,
    updateDisplayedDateByModelValue,
    submitText,
    getYearsRange,
    getRange,
    displayedMonth,
    displayedYear,
    displayedMonthYear,
    rangeDisplayedMonthYear,
    calendarEvents,
    rangeStart,
  };

  provide(CALENDAR_KEY, service);

  return service;
}

export function useCalendar() {
  const value = inject(CALENDAR_KEY);

  if (!value) {
    throw new Error('Call useCalendarModel first!');
  }

  return value;
}
